import { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import PricingCard from './components/PricingCard';
import ComparisonTable from './components/ComparisonTable';
import ROICalculator from './components/ROICalculator';
import PackageQuiz from './components/PackageQuiz';
import FAQSection from './components/FAQSection';
import TrustBadges from './components/TrustBadges';
import {
  PricingTier,
  ComparisonFeature,
  ROIMetric,
  FAQItem,
  QuizQuestion } from
'./types';
import { Helmet } from 'react-helmet-async';

import { CONTACT } from 'config/contact';

const Pricing = () => {
  const [showQuiz, setShowQuiz] = useState(false);
  const [recommendedPlan, setRecommendedPlan] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'monthly' | 'annual'>('monthly');

  const pricingTiers: PricingTier[] = [
  {
    id: 'starter',
    name: 'Starter',
    tagline: 'Perfect for small businesses getting started',
    price: activeTab === 'monthly' ? 132000 : 1320000,
    billingPeriod: activeTab === 'monthly' ? 'month' : 'year',
    description: 'Essential automation to streamline your core processes',
    features: [
    'Up to 5 automated workflows',
    '1,000 tasks per month',
    'Email & WhatsApp integration',
    'Basic chatbot setup',
    'Google Sheets automation',
    'Standard support (48hr response)',
    'Monthly performance reports',
    'Basic API integrations'],

    limitations: [
    'Limited to 5 workflows',
    'No custom integrations',
    'Standard support only',
    'No dedicated account manager'],

    recommended: false,
    ctaText: 'Start Automating',
    ctaVariant: 'outline',
    popular: false
  },
  {
    id: 'professional',
    name: 'Professional',
    tagline: 'For growing businesses scaling operations',
    price: activeTab === 'monthly' ? 308000 : 3080000,
    billingPeriod: activeTab === 'monthly' ? 'month' : 'year',
    description: 'Advanced automation with custom workflows and priority support',
    features: [
    'Up to 20 automated workflows',
    '10,000 tasks per month',
    'All Starter features',
    'Advanced AI chatbot with NLP',
    'Custom API integrations',
    'n8n & Make.com workflows',
    'CRM integration (Salesforce, HubSpot)',
    'Priority support (24hr response)',
    'Weekly performance analytics',
    'Dedicated account manager',
    'Quarterly strategy sessions'],

    limitations: ['Limited to 20 workflows', 'No white-label options'],
    recommended: true,
    ctaText: 'Scale Your Business',
    ctaVariant: 'default',
    popular: true
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    tagline: 'For large organizations with complex needs',
    price: activeTab === 'monthly' ? 660000 : 6600000,
    billingPeriod: activeTab === 'monthly' ? 'month' : 'year',
    description: 'Unlimited automation with white-label options and 24/7 support',
    features: [
    'Unlimited automated workflows',
    'Unlimited tasks per month',
    'All Professional features',
    'Custom AI model training',
    'White-label solutions',
    'Multi-platform integrations',
    'Advanced security & compliance',
    '24/7 priority support',
    'Real-time analytics dashboard',
    'Dedicated success team',
    'Monthly strategy sessions',
    'Custom SLA agreements',
    'On-site implementation support'],

    limitations: [],
    recommended: false,
    ctaText: 'Contact Sales',
    ctaVariant: 'secondary',
    popular: false
  }];


  const comparisonFeatures: ComparisonFeature[] = [
  {
    category: 'Workflow Automation',
    features: [
    {
      name: 'Number of Workflows',
      description: 'Maximum automated workflows you can create',
      starter: '5 workflows',
      professional: '20 workflows',
      enterprise: 'Unlimited'
    },
    {
      name: 'Monthly Task Limit',
      description: 'Number of automated tasks executed per month',
      starter: '1,000 tasks',
      professional: '10,000 tasks',
      enterprise: 'Unlimited'
    },
    {
      name: 'Workflow Complexity',
      description: 'Complexity level of automation workflows',
      starter: 'Basic',
      professional: 'Advanced',
      enterprise: 'Custom'
    }]

  },
  {
    category: 'Integration Capabilities',
    features: [
    {
      name: 'Email Integration',
      description: 'Gmail, Outlook automation',
      starter: true,
      professional: true,
      enterprise: true
    },
    {
      name: 'WhatsApp Business API',
      description: 'Automated WhatsApp messaging',
      starter: true,
      professional: true,
      enterprise: true
    },
    {
      name: 'CRM Integration',
      description: 'Salesforce, HubSpot, Zoho',
      starter: false,
      professional: true,
      enterprise: true
    },
    {
      name: 'Custom API Development',
      description: 'Build custom integrations',
      starter: false,
      professional: true,
      enterprise: true
    },
    {
      name: 'Multi-Platform Sync',
      description: 'Sync data across multiple platforms',
      starter: false,
      professional: 'Limited',
      enterprise: 'Unlimited'
    }]

  },
  {
    category: 'AI & Chatbot Features',
    features: [
    {
      name: 'Basic Chatbot',
      description: 'Rule-based chatbot automation',
      starter: true,
      professional: true,
      enterprise: true
    },
    {
      name: 'AI-Powered NLP',
      description: 'Natural language processing',
      starter: false,
      professional: true,
      enterprise: true
    },
    {
      name: 'Custom AI Training',
      description: 'Train AI on your specific data',
      starter: false,
      professional: false,
      enterprise: true
    },
    {
      name: 'Multi-Language Support',
      description: 'Chatbot in multiple languages',
      starter: false,
      professional: 'Up to 5',
      enterprise: 'Unlimited'
    }]

  },
  {
    category: 'Support & Services',
    features: [
    {
      name: 'Response Time',
      description: 'Support ticket response time',
      starter: '48 hours',
      professional: '24 hours',
      enterprise: '2 hours'
    },
    {
      name: 'Support Channels',
      description: 'Available support methods',
      starter: 'Email',
      professional: 'Email, Chat',
      enterprise: 'Email, Chat, Phone'
    },
    {
      name: 'Account Manager',
      description: 'Dedicated account management',
      starter: false,
      professional: true,
      enterprise: true
    },
    {
      name: 'Strategy Sessions',
      description: 'Regular optimization meetings',
      starter: false,
      professional: 'Quarterly',
      enterprise: 'Monthly'
    },
    {
      name: 'On-Site Support',
      description: 'In-person implementation help',
      starter: false,
      professional: false,
      enterprise: true
    }]

  },
  {
    category: 'Reporting & Analytics',
    features: [
    {
      name: 'Performance Reports',
      description: 'Automation performance tracking',
      starter: 'Monthly',
      professional: 'Weekly',
      enterprise: 'Real-time'
    },
    {
      name: 'Custom Dashboards',
      description: 'Personalized analytics views',
      starter: false,
      professional: true,
      enterprise: true
    },
    {
      name: 'ROI Tracking',
      description: 'Track return on investment',
      starter: false,
      professional: true,
      enterprise: true
    },
    {
      name: 'Advanced Analytics',
      description: 'Deep-dive performance insights',
      starter: false,
      professional: false,
      enterprise: true
    }]

  }];


  const trustMetrics: ROIMetric[] = [
  {
    label: 'Active Clients',
    value: '500+',
    description: 'Businesses automated',
    iconName: 'Users'
  },
  {
    label: 'Hours Saved',
    value: '2.5M+',
    description: 'Collectively per year',
    iconName: 'Clock'
  },
  {
    label: 'Workflows Created',
    value: '10K+',
    description: 'Automation solutions',
    iconName: 'Zap'
  },
  {
    label: 'Client Satisfaction',
    value: '98%',
    description: 'Would recommend us',
    iconName: 'Star'
  }];


  const faqs: FAQItem[] = [
  {
    id: '1',
    question: 'What happens if I exceed my monthly task limit?',
    answer:
    "We'll notify you when you reach 80% of your limit. You can either upgrade to a higher tier or purchase additional task blocks. We never stop your automations mid-month - we'll work with you to find the best solution for your needs."
  },
  {
    id: '2',
    question: 'Can I switch plans at any time?',
    answer:
    'Yes! You can upgrade or downgrade your plan at any time. When upgrading, you get immediate access to new features. When downgrading, changes take effect at your next billing cycle, and you retain current features until then.'
  },
  {
    id: '3',
    question: 'Do you offer custom pricing for unique requirements?',
    answer:
    'Absolutely. If none of our standard plans fit your needs, we can create a custom package. This is common for enterprises with specific compliance requirements, white-label needs, or unique integration demands. Contact our sales team for a tailored quote.'
  },
  {
    id: '4',
    question: 'What kind of support is included?',
    answer:
    'All plans include email support with varying response times. Professional and Enterprise plans include chat support and dedicated account managers. Enterprise clients also get phone support and quarterly/monthly strategy sessions to optimize their automation.'
  },
  {
    id: '5',
    question: 'Is there a setup fee?',
    answer:
    'No setup fees for Starter and Professional plans - we include initial setup in your first month. Enterprise plans may have a one-time implementation fee depending on complexity, but this is discussed upfront during the sales process.'
  },
  {
    id: '6',
    question: 'What happens to my data if I cancel?',
    answer:
    'You retain full ownership of your data. Upon cancellation, we provide a complete export of all your workflows, configurations, and data. We maintain your data for 90 days after cancellation in case you want to reactivate, then securely delete it per our data retention policy.'
  },
  {
    id: '7',
    question: 'Do you offer a money-back guarantee?',
    answer:
    'Yes! We offer a 30-day money-back guarantee on all plans. If you\'re not satisfied with our service within the first 30 days, we\'ll refund your payment in full, no questions asked. We\'re confident you\'ll see value immediately.'
  },
  {
    id: '8',
    question: 'Can I get a demo before committing?',
    answer:
    'Definitely! We offer free automation audits where we analyze your current processes and demonstrate how our solutions would work for your specific use case. Book a call with our team to see our platform in action with your actual workflows.'
  }];


  const quizQuestions: QuizQuestion[] = [
  {
    id: '1',
    question: 'How many team members will use the automation?',
    options: [
    {
      label: '1-5 people',
      value: 'small',
      tierWeight: { starter: 3, professional: 1, enterprise: 0 }
    },
    {
      label: '6-20 people',
      value: 'medium',
      tierWeight: { starter: 1, professional: 3, enterprise: 1 }
    },
    {
      label: '20+ people',
      value: 'large',
      tierWeight: { starter: 0, professional: 1, enterprise: 3 }
    }]

  },
  {
    id: '2',
    question: 'How many processes do you want to automate?',
    options: [
    {
      label: '1-3 core processes',
      value: 'few',
      tierWeight: { starter: 3, professional: 1, enterprise: 0 }
    },
    {
      label: '4-10 processes',
      value: 'several',
      tierWeight: { starter: 1, professional: 3, enterprise: 1 }
    },
    {
      label: '10+ processes',
      value: 'many',
      tierWeight: { starter: 0, professional: 1, enterprise: 3 }
    }]

  },
  {
    id: '3',
    question: 'What level of support do you need?',
    options: [
    {
      label: 'Email support is fine',
      value: 'basic',
      tierWeight: { starter: 3, professional: 1, enterprise: 0 }
    },
    {
      label: 'Need chat and account manager',
      value: 'priority',
      tierWeight: { starter: 0, professional: 3, enterprise: 1 }
    },
    {
      label: 'Need 24/7 support and dedicated team',
      value: 'premium',
      tierWeight: { starter: 0, professional: 1, enterprise: 3 }
    }]

  },
  {
    id: '4',
    question: 'Do you need custom integrations?',
    options: [
    {
      label: 'No, standard integrations work',
      value: 'standard',
      tierWeight: { starter: 3, professional: 1, enterprise: 0 }
    },
    {
      label: 'Yes, some custom APIs',
      value: 'some',
      tierWeight: { starter: 0, professional: 3, enterprise: 1 }
    },
    {
      label: 'Yes, extensive custom development',
      value: 'extensive',
      tierWeight: { starter: 0, professional: 1, enterprise: 3 }
    }]

  }];


  const handleQuizComplete = (recommendation: string) => {
    setRecommendedPlan(recommendation);
    setShowQuiz(false);
    const element = document.getElementById('pricing-tiers');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <>
      <Helmet>
        <title>Automation Plans & Pricing in INR | Make Mind Automations</title>
        <meta name="description" content="Transparent, value-based pricing in INR for business automation. Choose the perfect plan for your needs and scale with confidence." />
        <meta name="keywords" content="automation, business automation, workflow automation, AI chatbot, n8n, Make.com, WhatsApp automation, custom integrations" />
        <link rel="canonical" href="https://www.makemindautomations.tech/pricing" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Automation Plans & Pricing in INR | Make Mind Automations" />
        <meta property="og:description" content="Transparent, value-based pricing in INR for business automation. Choose the perfect plan for your needs and scale with confidence." />
        <meta property="og:url" content="https://www.makemindautomations.tech/pricing" />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header />

        <main className="w-full">
          <section className="relative bg-gradient-to-br from-primary/5 via-background to-accent/5 py-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
            <div className="absolute inset-0 bg-grid-pattern opacity-5" />
            <div className="max-w-7xl mx-auto relative z-10">
              <div className="text-center mb-12">
                <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-semibold mb-6">
                  <Icon name="Zap" size={16} />
                  Transparent Pricing
                </div>
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-text-primary mb-6">
                  Choose Your Automation Plan
                </h1>
                <p className="text-xl text-text-secondary max-w-3xl mx-auto mb-8">
                  Transparent, value-based pricing designed to scale with your business. No hidden
                  fees, no surprises - just powerful automation that delivers ROI.
                </p>

                <div className="flex items-center justify-center gap-4 mb-8">
                  <button
                    onClick={() => setActiveTab('monthly')}
                    className={`px-6 py-3 rounded-lg font-semibold transition-all duration-fast ${
                    activeTab === 'monthly' ? 'bg-primary text-primary-foreground' : 'bg-surface text-text-secondary hover:text-text-primary'}`
                    }>

                    Monthly
                  </button>
                  <button
                    onClick={() => setActiveTab('annual')}
                    className={`px-6 py-3 rounded-lg font-semibold transition-all duration-fast ${
                    activeTab === 'annual' ? 'bg-primary text-primary-foreground' : 'bg-surface text-text-secondary hover:text-text-primary'}`
                    }>

                    Annual
                    <span className="ml-2 text-xs bg-success text-success-foreground px-2 py-1 rounded-full">
                      Save 17%
                    </span>
                  </button>
                </div>

                <div className="flex flex-wrap items-center justify-center gap-4">
                  <Link to="/contact">
                    <Button variant="default" size="lg" iconName="Calendar" iconPosition="left">
                      Book Free Audit
                    </Button>
                  </Link>
                  <Button
                    variant="outline"
                    size="lg"
                    iconName="HelpCircle"
                    iconPosition="left"
                    onClick={() => setShowQuiz(true)}>

                    Find My Plan
                  </Button>
                  <a
                    href={CONTACT.whatsappLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block">

                    <Button variant="ghost" size="lg" iconName="MessageCircle" iconPosition="left">
                      Chat on WhatsApp
                    </Button>
                  </a>
                </div>
              </div>

              <TrustBadges metrics={trustMetrics} />
            </div>
          </section>

          {showQuiz &&
          <section className="py-16 px-4 sm:px-6 lg:px-8 bg-surface">
              <div className="max-w-3xl mx-auto">
                <PackageQuiz questions={quizQuestions} onComplete={handleQuizComplete} />
              </div>
            </section>
          }

          <section id="pricing-tiers" className="py-20 px-4 sm:px-6 lg:px-8">
            <div className="max-w-7xl mx-auto">
              {recommendedPlan &&
              <div className="mb-12 bg-success/10 border border-success rounded-xl p-6 text-center">
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <Icon name="CheckCircle" size={24} className="text-success" />
                    <h3 className="text-xl font-bold text-text-primary">
                      Recommended Plan: {recommendedPlan}
                    </h3>
                  </div>
                  <p className="text-text-secondary">
                    Based on your answers, the {recommendedPlan} plan is the best fit for your needs.
                  </p>
                </div>
              }

              <div className="grid lg:grid-cols-3 gap-8 mb-16">
                {pricingTiers.map((tier) =>
                <PricingCard key={tier.id} tier={tier} />
                )}
              </div>

              <div className="text-center mb-16">
                <p className="text-text-secondary mb-4">
                  Need a custom solution? We can tailor a plan specifically for your business.
                </p>
                <Link to="/contact">
                  <Button variant="outline" size="lg" iconName="Mail" iconPosition="left">
                    Request Custom Quote
                  </Button>
                </Link>
              </div>
            </div>
          </section>

          <section className="py-20 px-4 sm:px-6 lg:px-8 bg-surface">
            <div className="max-w-7xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold text-text-primary mb-4">
                  Calculate Your Potential Savings
                </h2>
                <p className="text-lg text-text-secondary max-w-2xl mx-auto">
                  See exactly how much time and money you could save with automation. Adjust the
                  values to match your business.
                </p>
              </div>
              <ROICalculator />
            </div>
          </section>

          <section className="py-20 px-4 sm:px-6 lg:px-8">
            <div className="max-w-7xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold text-text-primary mb-4">
                  Detailed Feature Comparison
                </h2>
                <p className="text-lg text-text-secondary max-w-2xl mx-auto">
                  Compare all features across our plans to find the perfect fit for your automation
                  needs.
                </p>
              </div>
              <ComparisonTable features={comparisonFeatures} />
            </div>
          </section>

          <section className="py-20 px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold text-text-primary mb-4">
                  Frequently Asked Questions
                </h2>
                <p className="text-lg text-text-secondary">
                  Everything you need to know about our pricing and plans.
                </p>
              </div>
              <FAQSection faqs={faqs} />
            </div>
          </section>

          <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-primary/10 via-background to-accent/10">
            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-primary rounded-full mb-6">
                <Icon name="Shield" size={32} color="white" />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-text-primary mb-4">
                30-Day Money-Back Guarantee
              </h2>
              <p className="text-lg text-text-secondary mb-8 max-w-2xl mx-auto">
                We're confident you'll love our automation solutions. If you're not completely
                satisfied within the first 30 days, we'll refund your payment in full - no questions
                asked.
              </p>
              <div className="flex flex-wrap items-center justify-center gap-4">
                <Link to="/contact">
                  <Button variant="default" size="lg" iconName="Calendar" iconPosition="left">
                    Start Your Free Audit
                  </Button>
                </Link>
                <a
                  href={CONTACT.whatsappLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block">

                  <Button variant="outline" size="lg" iconName="MessageCircle" iconPosition="left">
                    Chat on WhatsApp
                  </Button>
                </a>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default Pricing;